import {FC, forwardRef, ReactNode, Ref} from 'react'

import {BreadcrumbStep} from '@/app/lib/structuredData'
import {
  CONTENT_LAYOUT_SIDEBAR_WIDTH,
  CONTENT_LAYOUT_WIDTH,
  HEADER_HEIGHT_DESKTOP,
} from '@/theme/sizing'
import {theme} from '@/theme/theme'
import {getRelatedServiceContent} from '@/utils/related-services/helpers'
import {RelatedService} from '@/utils/related-services/types'
import {
  MOBILE_SCREENSIZE,
  TABLET_SCREENSIZE,
  USER_TABLET_SCREENSIZE,
} from '@/utils/styles/breakpoints'

import {Breadcrumbs} from './Breadcrumbs'
import {ContentCard, ContentCardImageSize} from './ContentCard'
import {Text, TextVariant} from './Text'

type ContentLayoutWithSidebarProps = {
  ref?: Ref<HTMLDivElement>
  // The same steps as the page's BreadcrumbList, so what is on screen matches it.
  breadcrumbTrail?: BreadcrumbStep[]
  // Leaf pages, whose h1 already says the last step. It stays in the BreadcrumbList.
  hideCurrentBreadcrumb?: boolean
  layoutTitle?: string
  layoutTitleVariant?: TextVariant
  relatedServices?: RelatedService[]
  sidebarCard?: ReactNode
}

export const ContentLayoutWithSidebar: FC<React.PropsWithChildren<ContentLayoutWithSidebarProps>> =
  forwardRef(
    (
      {
        breadcrumbTrail,
        hideCurrentBreadcrumb,
        layoutTitle,
        layoutTitleVariant = TextVariant.h1,
        relatedServices,
        sidebarCard,
        children,
      },
      ref,
    ) => {
      const hasSidebar = (relatedServices && relatedServices.length > 0) || sidebarCard

      return (
        <>
          <div className="layout" ref={ref}>
            <div className="wrapper">
              {breadcrumbTrail && (
                <div className="breadcrumbs">
                  <Breadcrumbs trail={breadcrumbTrail} hideCurrent={hideCurrentBreadcrumb} />
                </div>
              )}
              {layoutTitle ? (
                <>
                  <Text variant={layoutTitleVariant}>{layoutTitle}</Text>
                  <div className="content">{children}</div>
                </>
              ) : (
                children
              )}
            </div>
            {hasSidebar && (
              <div className="sidebar">
                {relatedServices
                  ? relatedServices.map((relatedService) => (
                      <ContentCard
                        key={relatedService.id}
                        imageSrc={relatedService.imageSrc}
                        imageAlt={relatedService.imageAlt}
                        imageSize={ContentCardImageSize.large}
                        title={relatedService.title}
                        titleTextVariant={TextVariant.h5}
                        // Promotions, not sections of the page - an h3 here skipped a level.
                        titleElement="span"
                        description={relatedService.description}
                        descriptionTextVariant={TextVariant.text16}
                        linkHref={getRelatedServiceContent(relatedService.service)?.linkUrl}
                        trackedEvent={{
                          name: `Related service card is clicked`,
                          parameters: {service: relatedService.service},
                        }}
                        buttonText={relatedService.buttonText}
                      />
                    ))
                  : sidebarCard}
              </div>
            )}
          </div>

          <style jsx>{`
            .layout {
              display: flex;
              justify-content: space-between;
            }
            .wrapper {
              flex: 1;
              max-width: ${hasSidebar ? CONTENT_LAYOUT_WIDTH : '100%'};
            }
            .breadcrumbs {
              margin-bottom: ${theme.spacing[5]};
            }
            .content {
              margin-top: ${theme.spacing[7]};
            }
            .sidebar {
              position: sticky;
              top: calc(${HEADER_HEIGHT_DESKTOP} + ${theme.spacing[4]});
              height: 100%;
              flex: 0 0 ${CONTENT_LAYOUT_SIDEBAR_WIDTH};
              max-width: ${CONTENT_LAYOUT_SIDEBAR_WIDTH};
              margin-left: ${theme.spacing[7]};
              display: flex;
              flex-direction: column;
              gap: ${theme.spacing[4]};
            }
            @media ${TABLET_SCREENSIZE} {
              .sidebar {
                top: ${theme.spacing[5]};
              }
            }
            @media ${USER_TABLET_SCREENSIZE} {
              .wrapper {
                max-width: inherit;
              }
              .sidebar {
                display: none;
              }
            }
            @media ${MOBILE_SCREENSIZE} {
              .content {
                margin-top: ${theme.spacing[6]};
              }
            }
          `}</style>
        </>
      )
    },
  )
